import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Sparkles, CheckCircle2, Plus, ChevronUp, ChevronDown, Mail, MessageCircle, X, GripVertical, Wand2, Users, Send } from 'lucide-react';
import {
  getCommsIntelGuestInsights,
  getJourneySteps,
  createJourneyStep,
  updateJourneyStep,
  reorderJourneySteps,
  getJourneyStepCompletion,
  getEmailTemplates,
  getSmsTemplates,
} from '../../../data/api';
import { initials, guestDisplayName, shortDate, rsvpIsYes } from './ciUtils';
import { Loading } from './CiShared';
import './journeyScreen.css';

// View Journey — two truthful layers:
//   1. Per-guest journey (timeline + attendance likelihood/confidence) read
//      live from the insights endpoint. A guest picker drives this view.
//   2. The journey definition every guest flows through — the shared
//      journey_steps. This is the editable layer: Add step, Reorder
//      (drag-and-drop OR move up/down -> reorderJourneySteps), Edit
//      name/template/timing, and enable/disable (-> updateJourneyStep, which
//      persists). These steps are global (the plan), not per-guest, and the UI
//      labels them that way.
//
// This screen has its own guest picker, so it works with NO preselected guest
// (initialGuestId may be null — we default to the first guest, as before).

const ENABLED = 'active';
const DISABLED = 'paused';
const isEnabled = (status) => String(status || '').toLowerCase() === ENABLED;
const OFFSET_UNITS = ['minutes', 'hours', 'days', 'weeks'];

// Same slug rule the backend uses to derive step_code from a name, so we can
// detect which recommended milestones already exist (survives renames of OTHER
// steps because we match on the recommended code, not display text).
const slugify = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// The recommended "happy path" sequence for a wedding-style event. Offsets are
// relative to the event (negative = before). createJourneyStep auto-assigns an
// ascending `order` when omitted, so creating these in sequence yields the
// right order out of the box.
const RECOMMENDED_JOURNEY = [
  { name: 'Save the Date', channel: 'email', timing: 'before', offsetValue: 90, offsetUnit: 'days' },
  { name: 'Official Invitation', channel: 'email', timing: 'before', offsetValue: 45, offsetUnit: 'days' },
  { name: 'Reminder', channel: 'email', timing: 'before', offsetValue: 14, offsetUnit: 'days' },
  { name: 'Final Reminder', channel: 'sms', timing: 'before', offsetValue: 2, offsetUnit: 'days' },
  { name: 'Thank-You', channel: 'email', timing: 'after', offsetValue: 3, offsetUnit: 'days' },
];
const RECOMMENDED_CODES = new Set(RECOMMENDED_JOURNEY.map((r) => slugify(r.name)));

function recommendedToPayload(r) {
  const magnitude = Math.abs(Number(r.offsetValue) || 0);
  const signed = r.timing === 'before' ? -magnitude : magnitude;
  return {
    name: r.name,
    channel: r.channel,
    template_id: null,
    timing_mode: 'offset',
    fixed_at: null,
    offset_value: signed,
    offset_unit: r.offsetUnit || 'days',
    status: ENABLED,
  };
}

// Which recommended milestones are NOT already represented in the current steps.
// Match by derived step_code (preferred) and fall back to a slug of the name.
function missingRecommended(steps) {
  const present = new Set();
  (steps || []).forEach((s) => {
    if (s.step_code) present.add(String(s.step_code).toLowerCase());
    if (s.name) present.add(slugify(s.name));
  });
  return RECOMMENDED_JOURNEY.filter((r) => !present.has(slugify(r.name)));
}

function stepTimingLabel(step) {
  if (step.timing_mode === 'fixed_datetime' && step.fixed_at) {
    try {
      const dt = new Date(step.fixed_at);
      if (!Number.isNaN(dt.getTime())) return `On ${dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } catch { /* keep raw */ }
    return `On ${step.fixed_at}`;
  }
  const v = Number(step.offset_value ?? 0);
  const unit = step.offset_unit || 'days';
  if (v < 0) return `${Math.abs(v)} ${unit} before the event`;
  if (v === 0) return 'At the trigger';
  return `${v} ${unit} after the trigger`;
}

const blankStepDraft = () => ({
  name: '',
  channel: 'email',
  templateId: '',
  timing: 'after',
  offsetValue: 7,
  offsetUnit: 'days',
  fixedAt: '',
});

function toLocalInput(value) {
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  } catch {
    return '';
  }
}

export default function JourneyScreen({ guests = [], initialGuestId, onBack, onPrepareSend }) {
  const eligible = useMemo(
    () => (guests || []).filter((g) => g && (g._id || g.id)),
    [guests],
  );
  const [guestId, setGuestId] = useState(initialGuestId || (eligible[0] ? String(eligible[0]._id || eligible[0].id) : ''));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  // Shared journey-definition state.
  const [steps, setSteps] = useState(null);
  const [stepsErr, setStepsErr] = useState(false);
  const [emailTpls, setEmailTpls] = useState([]);
  const [smsTpls, setSmsTpls] = useState([]);
  const [busyId, setBusyId] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | step.id
  const [draft, setDraft] = useState(blankStepDraft());
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [seeding, setSeeding] = useState(false); // quick-start in progress

  // Per-step "X of Y guests reached" stats, keyed by step id.
  // completion === null -> loading; {} -> loaded/empty; entries -> { reached, total }.
  const [completion, setCompletion] = useState(null);
  const [completionTotal, setCompletionTotal] = useState(0);

  // Drag-and-drop state for the shared steps list.
  const [dragIndex, setDragIndex] = useState(null);   // index being dragged
  const [overIndex, setOverIndex] = useState(null);   // index currently hovered

  const flash = (m) => { setNote(m); window.clearTimeout(flash._t); flash._t = window.setTimeout(() => setNote(''), 2600); };

  useEffect(() => {
    if (!guestId) { setData(null); return undefined; }
    let alive = true;
    setLoading(true);
    setErr(false);
    getCommsIntelGuestInsights(guestId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setErr(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [guestId]);

  const loadSteps = () => {
    setStepsErr(false);
    getJourneySteps()
      .then((d) => setSteps(Array.isArray(d) ? d : []))
      .catch(() => { setStepsErr(true); setSteps([]); });
  };

  // Pull completion stats. Normalises both the documented [{step_id,reached,total}]
  // shape and the actual { steps:[...], total_eligible_guests } shape.
  const loadCompletion = () => {
    getJourneyStepCompletion()
      .then((d) => {
        const rows = Array.isArray(d) ? d : (Array.isArray(d?.steps) ? d.steps : []);
        const map = {};
        let maxTotal = Number(d?.total_eligible_guests || d?.total_guests || 0) || 0;
        rows.forEach((r) => {
          const id = String(r.step_id ?? r.id ?? '');
          if (!id) return;
          const reached = Number(r.reached || 0);
          const total = Number(r.total ?? d?.total_eligible_guests ?? 0) || 0;
          if (total > maxTotal) maxTotal = total;
          map[id] = { reached, total };
        });
        setCompletion(map);
        setCompletionTotal(maxTotal);
      })
      .catch(() => { setCompletion({}); }); // graceful: stats simply won't show
  };

  useEffect(() => {
    loadSteps();
    loadCompletion();
    getEmailTemplates().then((d) => setEmailTpls(Array.isArray(d) ? d : [])).catch(() => setEmailTpls([]));
    getSmsTemplates().then((d) => setSmsTpls(Array.isArray(d) ? d : [])).catch(() => setSmsTpls([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guest = eligible.find((g) => String(g._id || g.id) === guestId) || null;
  const name = data?.guest_name || (guest ? guestDisplayName(guest) : 'Guest');
  const al = data?.attendance_likelihood || {};
  const confirmed = al.confirmed || (guest && rsvpIsYes(guest.rsvp_status));
  const score = Number(data?.engagement_score || 0);

  const tplOptions = draft.channel === 'sms' ? smsTpls : emailTpls;
  const tplName = useMemo(() => {
    const map = {};
    [...emailTpls, ...smsTpls].forEach((t) => { map[String(t.id || t._id)] = t.name; });
    return (id) => (id ? (map[String(id)] || 'Selected template') : null);
  }, [emailTpls, smsTpls]);

  const draftToPayload = (d) => {
    const base = { name: d.name.trim() || 'Untitled step', channel: d.channel, template_id: d.templateId || null };
    if (d.timing === 'fixed') {
      return { ...base, timing_mode: 'fixed_datetime', fixed_at: d.fixedAt || null, offset_value: null, offset_unit: null };
    }
    const magnitude = Math.abs(Number(d.offsetValue) || 0);
    const signed = d.timing === 'before' ? -magnitude : magnitude;
    return { ...base, timing_mode: 'offset', fixed_at: null, offset_value: signed, offset_unit: d.offsetUnit || 'days' };
  };

  const openNew = () => { setDraft(blankStepDraft()); setEditing('new'); };
  const openEdit = (step) => {
    let timing = 'after';
    if (step.timing_mode === 'fixed_datetime') timing = 'fixed';
    else if ((step.offset_value ?? 0) < 0) timing = 'before';
    setDraft({
      name: step.name || '',
      channel: step.channel || 'email',
      templateId: step.template_id || '',
      timing,
      offsetValue: Math.abs(Number(step.offset_value ?? 0)) || 0,
      offsetUnit: step.offset_unit || 'days',
      fixedAt: step.timing_mode === 'fixed_datetime' && step.fixed_at ? toLocalInput(step.fixed_at) : '',
    });
    setEditing(step.id);
  };
  const closeEditor = () => { setEditing(null); setSaving(false); };

  const saveDraft = async () => {
    if (saving) return;
    setSaving(true);
    const payload = draftToPayload(draft);
    try {
      if (editing === 'new') {
        await createJourneyStep({ ...payload, status: ENABLED });
        flash('Step added to the journey');
      } else {
        await updateJourneyStep(editing, payload);
        flash('Step updated');
      }
      closeEditor();
      loadSteps();
      loadCompletion();
    } catch {
      flash(editing === 'new' ? 'Could not add step' : 'Could not save step');
      setSaving(false);
    }
  };

  // Single source of truth for persisting a new order. Optimistically applies
  // `nextSteps`, then writes the FULL normalised order (order = index) for every
  // step so a multi-position move sticks — not just a 2-item swap. Reverts to the
  // previous array + toasts on failure.
  const persistOrder = async (nextSteps, prevSteps, okMsg) => {
    setSteps(nextSteps);
    const payload = nextSteps.map((s, idx) => ({ id: s.id, order: idx }));
    try {
      await reorderJourneySteps(payload);
      flash(okMsg || 'Journey reordered');
      loadSteps();
    } catch {
      setSteps(prevSteps); // revert
      flash('Could not reorder — try again');
    }
  };

  // Up/down chevrons — accessibility fallback for drag-and-drop.
  const move = async (index, dir) => {
    if (busyId || seeding || !steps) return;
    const target = index + dir;
    if (target < 0 || target >= steps.length) return;
    const a = steps[index];
    setBusyId(a.id);
    const prev = steps;
    const reordered = steps.slice();
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    try {
      await persistOrder(reordered, prev, 'Journey reordered');
    } finally {
      setBusyId('');
    }
  };

  // --- Native HTML5 drag-and-drop handlers -------------------------------
  const dndDisabled = (id) => !!busyId || seeding || !!editing || busyId === id;

  const onStepDragStart = (e, index) => {
    if (dndDisabled(steps?.[index]?.id)) { e.preventDefault(); return; }
    setDragIndex(index);
    setOverIndex(index);
    try {
      e.dataTransfer.effectAllowed = 'move';
      // Some browsers require data to be set for a drag to start.
      e.dataTransfer.setData('text/plain', String(index));
    } catch { /* non-fatal */ }
  };

  const onStepDragOver = (e, index) => {
    if (dragIndex === null) return;
    e.preventDefault();          // allow drop
    try { e.dataTransfer.dropEffect = 'move'; } catch { /* non-fatal */ }
    if (overIndex !== index) setOverIndex(index);
  };

  const clearDrag = () => { setDragIndex(null); setOverIndex(null); };

  const onStepDrop = async (e, index) => {
    e.preventDefault();
    const from = dragIndex;
    clearDrag();
    if (from === null || !steps) return;
    if (from === index) return; // no movement
    const prev = steps;
    const reordered = steps.slice();
    const [moved] = reordered.splice(from, 1);
    reordered.splice(index, 0, moved);
    setBusyId(moved.id);
    try {
      await persistOrder(reordered, prev, 'Journey reordered — saved');
    } finally {
      setBusyId('');
    }
  };

  const toggle = async (step) => {
    if (busyId || seeding) return;
    setBusyId(step.id);
    const next = isEnabled(step.status) ? DISABLED : ENABLED;
    setSteps((arr) => (arr || []).map((s) => (s.id === step.id ? { ...s, status: next } : s)));
    try {
      await updateJourneyStep(step.id, { status: next });
      flash(next === ENABLED ? 'Step enabled' : 'Step disabled');
    } catch {
      setSteps((arr) => (arr || []).map((s) => (s.id === step.id ? { ...s, status: step.status } : s)));
      flash('Could not update — try again');
    } finally {
      setBusyId('');
    }
  };

  // Quick-start: create a list of recommended steps in sequence. Used for both
  // "Set up the recommended journey" (zero steps) and "Add missing milestones".
  const seedJourney = async (recommended) => {
    if (seeding || !recommended || recommended.length === 0) return;
    setSeeding(true);
    let created = 0;
    try {
      // Sequential so backend's max-order auto-assign yields ascending order.
      for (const r of recommended) {
        // eslint-disable-next-line no-await-in-loop
        await createJourneyStep(recommendedToPayload(r));
        created += 1;
      }
      flash(created === 1 ? 'Added 1 milestone' : `Added ${created} milestones`);
    } catch {
      flash(created > 0 ? `Added ${created} — then hit a snag. Try again for the rest.` : 'Could not set up the journey — try again');
    } finally {
      setSeeding(false);
      loadSteps();
      loadCompletion();
    }
  };

  const missing = useMemo(() => missingRecommended(steps), [steps]);

  return (
    <div className="ci-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <button className="ci-btn ci-sm" onClick={onBack}><ArrowLeft size={13} /> Back to Communications</button>
        {eligible.length > 0 && (
          <select className="ci-inp" style={{ maxWidth: 280 }} value={guestId} onChange={(e) => setGuestId(e.target.value)}>
            {eligible.map((g) => (
              <option key={String(g._id || g.id)} value={String(g._id || g.id)}>{guestDisplayName(g)}</option>
            ))}
          </select>
        )}
      </div>

      {!guestId && <div className="ci-glass" style={{ padding: 26 }}><div className="ci-empty">No guests available to show a journey for.</div></div>}

      {guestId && (
        <>
          <div className="ci-jhead">
            <div className="ci-who">
              <div className="ci-av">{initials(name)}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  {name}
                  {guest?.is_vip && <span className="ci-vip" style={{ marginLeft: 8 }}>VIP</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ci-txt2)', marginTop: 2 }}>
                  Status: <b style={{ color: confirmed ? 'var(--ci-green)' : 'var(--ci-txt)' }}>{confirmed ? 'Attending' : (guest?.rsvp_status || 'Pending')}</b>
                </div>
              </div>
            </div>
            <div className="ci-jscore">
              <div className="ci-n">{loading ? '…' : score}</div>
              <div className="ci-l">Engagement Score</div>
            </div>
          </div>

          {loading && <Loading label="Building journey…" />}
          {err && <div className="ci-glass" style={{ padding: 26 }}><div className="ci-empty">Couldn't load this guest's journey right now.</div></div>}

          {!loading && !err && data && (
            <div className="ci-jcard">
              <div className="ci-glass ci-jtl">
                <div className="ci-ins-h" style={{ marginBottom: 6 }}>Communication Journey</div>
                {Array.isArray(data.timeline) && data.timeline.length > 0 ? (
                  data.timeline.map((ev, i) => (
                    <div className="ci-jevent" key={i}>
                      <div className="ci-jd">{i === 0 ? '◆' : '○'}</div>
                      <div>
                        <div className="ci-jt">{ev.label}</div>
                        <div className="ci-jdt">{shortDate(ev.at)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="ci-empty" style={{ textAlign: 'left' }}>No recorded activity yet for this guest.</div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="ci-glass" style={{ padding: 16 }}>
                  <div className="ci-ins-h" style={{ marginBottom: 8 }}><Sparkles size={15} /> Journey Insights</div>
                  {confirmed ? (
                    <div className="ci-confirmed-badge"><span className="ci-ck"><CheckCircle2 size={13} /></span> Confirmed — RSVP'd Yes</div>
                  ) : null}
                  <div className="ci-pred-metrics" style={{ marginTop: confirmed ? 8 : 0 }}>
                    <div className="ci-pred-m">
                      <div className="ci-pl">Attendance</div>
                      <div className="ci-pv ci-green">{al.value_pct ?? '—'}%</div>
                      <div className="ci-pred-bar"><i className="ci-green" style={{ width: `${al.value_pct || 0}%` }} /></div>
                    </div>
                    <div className="ci-pred-m">
                      <div className="ci-pl">Confidence</div>
                      <div className="ci-pv">{al.confidence_pct ?? '—'}%</div>
                      <div className="ci-pred-bar"><i style={{ width: `${al.confidence_pct || 0}%` }} /></div>
                    </div>
                  </div>
                  {Array.isArray(al.basis) && al.basis.length > 0 && (
                    <div className="ci-why-sig">
                      <div className="ci-wl">Why — signals</div>
                      <ul>{al.basis.map((b, i) => <li key={i}><span className="ci-s">✓</span><span>{b}</span></li>)}</ul>
                    </div>
                  )}
                </div>

                {/* Shared journey definition — the editable plan every guest flows through. */}
                <div className="ci-glass" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div className="ci-ins-h" style={{ marginBottom: 0 }}>Journey steps</div>
                    <button className="ci-btn ci-sm" onClick={openNew} disabled={seeding}><Plus size={12} /> Add step</button>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginBottom: 10 }}>
                    The shared plan every guest moves through. Drag to reorder (or use the arrows), edit, or enable/disable a step — changes persist for the whole event.
                  </div>

                  {steps === null && <Loading label="Loading steps…" />}
                  {stepsErr && (
                    <div className="ci-empty" style={{ textAlign: 'left' }}>
                      Journey steps are unavailable right now. <button className="ci-btn ci-sm" style={{ marginTop: 8 }} onClick={() => { loadSteps(); loadCompletion(); }}>Try again</button>
                    </div>
                  )}

                  {/* Quick-start when there are NO steps yet. */}
                  {steps && !stepsErr && steps.length === 0 && editing !== 'new' && (
                    <div className="js-quickstart">
                      <div className="js-qs-title"><Wand2 size={14} /> Start with a recommended journey</div>
                      <div className="js-qs-sub">
                        We'll create a sensible default sequence — Save the Date, Official Invitation, Reminder,
                        Final Reminder, and Thank-You. You can edit, reorder, or remove any of them afterward.
                      </div>
                      <ol className="js-qs-list">
                        {RECOMMENDED_JOURNEY.map((r) => (
                          <li key={slugify(r.name)}>
                            {r.channel === 'sms' ? <MessageCircle size={11} /> : <Mail size={11} />} {r.name}
                            <span className="js-qs-when">· {r.timing === 'before' ? `${r.offsetValue} ${r.offsetUnit} before` : `${r.offsetValue} ${r.offsetUnit} after`}</span>
                          </li>
                        ))}
                      </ol>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button className="ci-btn ci-gold ci-sm" onClick={() => seedJourney(RECOMMENDED_JOURNEY)} disabled={seeding}>
                          <Wand2 size={12} /> {seeding ? 'Setting up…' : 'Set up the recommended journey'}
                        </button>
                        <button className="ci-btn ci-sm" onClick={openNew} disabled={seeding}>Start from scratch</button>
                      </div>
                    </div>
                  )}

                  {/* "Add missing milestones" when SOME steps already exist. */}
                  {steps && !stepsErr && steps.length > 0 && missing.length > 0 && (
                    <div className="js-missing">
                      <span className="js-missing-txt">
                        <Wand2 size={12} /> Missing {missing.length === 1 ? 'a recommended milestone' : `${missing.length} recommended milestones`}: {missing.map((m) => m.name).join(', ')}.
                      </span>
                      <button className="ci-btn ci-ghost ci-sm" onClick={() => seedJourney(missing)} disabled={seeding}>
                        {seeding ? 'Adding…' : 'Add missing milestones'}
                      </button>
                    </div>
                  )}

                  {steps && !stepsErr && steps.map((step, i) => {
                    const on = isEnabled(step.status);
                    const Icon = step.channel === 'sms' ? MessageCircle : Mail;
                    const tpl = tplName(step.template_id);
                    const isDragging = dragIndex === i;
                    const isDropTarget = dragIndex !== null && overIndex === i && dragIndex !== i;
                    const dropBelow = isDropTarget && dragIndex < i; // indicator side
                    const reach = completion ? completion[String(step.id)] : undefined;
                    const reachTotal = reach ? reach.total : completionTotal;
                    const canDrag = !dndDisabled(step.id);
                    const rowClass = [
                      'js-step-row',
                      isDragging ? 'js-dragging' : '',
                      isDropTarget ? (dropBelow ? 'js-drop-below' : 'js-drop-above') : '',
                    ].filter(Boolean).join(' ');
                    return (
                      <div
                        key={step.id}
                        className={rowClass}
                        draggable={canDrag}
                        onDragStart={(e) => onStepDragStart(e, i)}
                        onDragOver={(e) => onStepDragOver(e, i)}
                        onDragEnter={(e) => onStepDragOver(e, i)}
                        onDrop={(e) => onStepDrop(e, i)}
                        onDragEnd={clearDrag}
                      >
                        <span
                          className="js-grip"
                          aria-hidden="true"
                          title={canDrag ? 'Drag to reorder' : 'Finish editing to reorder'}
                          style={{ cursor: canDrag ? 'grab' : 'default', opacity: canDrag ? 1 : 0.35 }}
                        >
                          <GripVertical size={14} />
                        </span>
                        <div className="js-step-rank">
                          <button className="js-move-btn" aria-label="Move up" disabled={i === 0 || busyId === step.id || seeding || !!editing} onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                          <span className="js-step-num">{i + 1}</span>
                          <button className="js-move-btn" aria-label="Move down" disabled={i === steps.length - 1 || busyId === step.id || seeding || !!editing} onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, opacity: on ? 1 : 0.55 }}>
                            <Icon size={12} style={{ verticalAlign: -1, marginRight: 5 }} />{step.name || 'Untitled step'}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 2 }}>
                            {stepTimingLabel(step)}
                            {' · '}
                            {tpl ? tpl : <span style={{ color: 'var(--ci-gold-hi)' }}>No template</span>}
                          </div>
                          {/* X of Y guests reached */}
                          <div className="js-reach" title="Distinct guests with a sent/delivered message for this step">
                            <Users size={11} />
                            {completion === null ? (
                              <span className="js-reach-muted">Loading reach…</span>
                            ) : reach ? (
                              <>
                                <b>{reach.reached}</b>
                                <span className="js-reach-muted"> of {reachTotal || 0} guests reached</span>
                                {reachTotal > 0 && (
                                  <span className="js-reach-bar"><i style={{ width: `${Math.min(100, Math.round((reach.reached / reachTotal) * 100))}%` }} /></span>
                                )}
                              </>
                            ) : (
                              <span className="js-reach-muted">{reachTotal ? `0 of ${reachTotal} guests reached yet` : 'No guests reached yet'}</span>
                            )}
                          </div>
                        </div>
                        {onPrepareSend && (
                          <button
                            className="ci-btn ci-sm"
                            onClick={() => onPrepareSend({
                              type: step.channel === 'sms' ? 'sms' : 'email',
                              templateId: step.template_id || '',
                              segment: 'pending',
                              intent: `Journey · ${step.name || 'milestone'}`,
                            })}
                            disabled={seeding}
                            title="Open Create with this milestone's channel, template, and pending-RSVP audience preset"
                          ><Send size={12} /> Prepare in Send</button>
                        )}
                        <button className="ci-btn ci-ghost ci-sm" onClick={() => openEdit(step)} disabled={seeding}>Edit</button>
                        <button
                          className={`ci-toggle ${on ? '' : 'ci-off'}`}
                          aria-label={`${on ? 'Disable' : 'Enable'} ${step.name}`}
                          disabled={busyId === step.id || seeding}
                          onClick={() => toggle(step)}
                        />
                      </div>
                    );
                  })}

                  {(editing === 'new' || (editing && steps && steps.some((s) => s.id === editing))) && (
                    <StepEditor
                      mode={editing === 'new' ? 'new' : 'edit'}
                      draft={draft}
                      setDraft={setDraft}
                      tplOptions={tplOptions}
                      saving={saving}
                      onSave={saveDraft}
                      onCancel={closeEditor}
                    />
                  )}

                  {note && <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ci-gold-hi)' }}>{note}</div>}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StepEditor({ mode, draft, setDraft, tplOptions, saving, onSave, onCancel }) {
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const showOffset = draft.timing !== 'fixed';
  const offsetWord = draft.timing === 'before' ? 'before the event' : 'after the trigger';

  return (
    <div className="ci-glass" style={{ padding: 14, marginTop: 10, borderColor: 'var(--ci-bdgold)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
        <div className="ci-ins-h" style={{ marginBottom: 0, fontSize: 12.5 }}>{mode === 'new' ? 'Add step' : 'Edit step'}</div>
        <button className="ci-kebab" aria-label="Close" onClick={onCancel}><X size={15} /></button>
      </div>

      <div className="ci-lab" style={{ marginTop: 8 }}>Step name</div>
      <input className="ci-inp" value={draft.name} placeholder="e.g. Reminder — Wave 2" onChange={(e) => set({ name: e.target.value })} />

      <div className="ci-lab">Channel</div>
      <div className="ci-seg">
        {[{ k: 'email', L: 'Email', I: Mail }, { k: 'sms', L: 'SMS', I: MessageCircle }].map(({ k, L, I }) => (
          <span key={k} className={`ci-s ${draft.channel === k ? 'ci-on' : ''}`} onClick={() => set({ channel: k, templateId: '' })}>
            <I size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{L}
          </span>
        ))}
      </div>

      <div className="ci-lab">Template</div>
      <select className="ci-inp" value={draft.templateId} onChange={(e) => set({ templateId: e.target.value })}>
        <option value="">No template (assign later)</option>
        {tplOptions.map((t) => (
          <option key={String(t.id || t._id)} value={String(t.id || t._id)}>{t.name || 'Untitled template'}</option>
        ))}
      </select>

      <div className="ci-lab">Timing</div>
      <select className="ci-inp" value={draft.timing} onChange={(e) => set({ timing: e.target.value })}>
        <option value="after">A delay after the trigger</option>
        <option value="before">Days before the event</option>
        <option value="fixed">On a fixed date</option>
      </select>

      {showOffset ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="number" min="0" className="ci-inp" style={{ maxWidth: 90 }} value={draft.offsetValue} onChange={(e) => set({ offsetValue: e.target.value })} />
          <select className="ci-inp" style={{ maxWidth: 120 }} value={draft.offsetUnit} onChange={(e) => set({ offsetUnit: e.target.value })}>
            {OFFSET_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--ci-txt3)' }}>{offsetWord}</span>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <input type="datetime-local" className="ci-inp" value={draft.fixedAt} onChange={(e) => set({ fixedAt: e.target.value })} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button className="ci-btn ci-gold ci-sm" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : (mode === 'new' ? 'Add step' : 'Save changes')}</button>
        <button className="ci-btn ci-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}
